"use client";

import { LazyMotion, domAnimation, m, useAnimation } from "framer-motion";
import type { HTMLAttributes, Ref } from "react";
import { useCallback, useImperativeHandle, useRef } from "react";

import { MOTION_DURATION, MOTION_REPEAT } from "@/lib/motion-theme";
import { cn } from "@/lib/utils";

export interface AudioLinesIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

interface AudioLinesIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
  ref?: Ref<AudioLinesIconHandle>;
}

function AudioLinesIcon({ onMouseEnter, onMouseLeave, className, size = 28, ref, ...props }: AudioLinesIconProps) {
    const controls = useAnimation();
    const isControlledRef = useRef(false);

    useImperativeHandle(ref, () => {
      isControlledRef.current = true;

      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal"),
      };
    });

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseEnter?.(e);
        } else {
          controls.start("animate");
        }
      },
      [controls, onMouseEnter]
    );

    const handleMouseLeave = useCallback(
      (e: React.MouseEvent<HTMLDivElement>) => {
        if (isControlledRef.current) {
          onMouseLeave?.(e);
        } else {
          controls.start("normal");
        }
      },
      [controls, onMouseLeave]
    );

    return (
      <div
        className={cn(className)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        {...props}
      >
        <svg
          fill="none"
          height={size}
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="2"
          viewBox="0 0 24 24"
          width={size}
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M2 10v3" />
          <LazyMotion features={domAnimation}>
            <m.path
              initial="normal"
              animate={controls}
              d="M6 6v11"
              variants={{
                normal: { d: "M6 6v11" },
                animate: {
                  d: ["M6 6v11", "M6 10v3", "M6 6v11"],
                  transition: {
                    duration: MOTION_DURATION.audioLoopSlow,
                    repeat: MOTION_REPEAT.loop,
                  },
                },
              }}
            />
            <m.path
              initial="normal"
              animate={controls}
              d="M10 3v18"
              variants={{
                normal: { d: "M10 3v18" },
                animate: {
                  d: ["M10 3v18", "M10 9v5", "M10 3v18"],
                  transition: {
                    duration: MOTION_DURATION.audioLoopStandard,
                    repeat: MOTION_REPEAT.loop,
                  },
                },
              }}
            />
            <m.path
              initial="normal"
              animate={controls}
              d="M14 8v7"
              variants={{
                normal: { d: "M14 8v7" },
                animate: {
                  d: ["M14 8v7", "M14 6v11", "M14 8v7"],
                  transition: {
                    duration: MOTION_DURATION.audioLoopFast,
                    repeat: MOTION_REPEAT.loop,
                  },
                },
              }}
            />
            <m.path
              initial="normal"
              animate={controls}
              d="M18 5v13"
              variants={{
                normal: { d: "M18 5v13" },
                animate: {
                  d: ["M18 5v13", "M18 7v9", "M18 5v13"],
                  transition: {
                    duration: MOTION_DURATION.audioLoopSlow,
                    repeat: MOTION_REPEAT.loop,
                  },
                },
              }}
            />
          </LazyMotion>
          <path d="M22 10v3" />
        </svg>
      </div>
    );
}

AudioLinesIcon.displayName = "AudioLinesIcon";

export { AudioLinesIcon };
